import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import moment from 'moment';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { getTelemetryConfig } from 'app/app-config';
import { LoggerService, LoggerAdditional } from 'app/services/logger.service';
import { AuthService } from './bridge-api/auth.service';

import { ACTION_PLAN_URL_PATH } from './storage-api/actionplan.service';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor, OnDestroy {
  prevExpiration: moment.Moment;
  expirationTimeout: NodeJS.Timeout;

  constructor(
    private authService: AuthService,
    private router: Router,
    private logger: LoggerService
  ) {
    this.prevExpiration = null;
  }

  ngOnDestroy() {
    if (this.expirationTimeout) {
      clearTimeout(this.expirationTimeout);
      this.expirationTimeout = undefined;
    }
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          const httpEvent = event as HttpResponse<any>;
          if (httpEvent.headers.has('X-session-expiration')) {
            const expiration = moment(httpEvent.headers.get('X-session-expiration'));
            if (!this.prevExpiration || expiration > this.prevExpiration) {
              this.authService.setExpiration(expiration);
              const relative = moment().to(expiration);
              this.logger.debug(
                `Session expires in ${relative} at ${expiration.format()} according to ${
                  httpEvent.url
                }`
              );
              this.prevExpiration = expiration;
            }
          }
        }
      }),
      catchError((error: HttpErrorResponse) => {
        if (this.isSessionExpired(error)) {
          this.handleSessionExpired();
        } else if (this.isLoggable(error)) {
          const additional: LoggerAdditional = {
            status: error.status,
            error: error.error,
            request: {
              url: error.url,
            },
          };
          if (request.body) {
            additional.request.body = request.body;
          }
          this.logger.error('HttpError', additional);
        }
        return throwError(() => error);
      })
    );
  }

  handleSessionExpired(): void {
    this.authService.clear('SessionExpired');
  }

  isSessionExpired(error: HttpErrorResponse): boolean {
    if (!error || !error.url || error.status !== 401) {
      return false;
    }

    if (!this.authService.isLoggedIn()) {
      return false;
    }

    const telemetryConfig = getTelemetryConfig();
    if (telemetryConfig.enabled && error.url === telemetryConfig.url) {
      return false;
    }

    if (error.url.includes(ACTION_PLAN_URL_PATH)) {
      return false;
    }

    return true;
  }

  isLoggable(error: HttpErrorResponse): boolean {
    if (!error || !error.url) {
      return false;
    }

    if (error.url.endsWith('/conf')) {
      return false;
    }

    const telemetryConfig = getTelemetryConfig();
    if (telemetryConfig.enabled && error.url === telemetryConfig.url) {
      return false;
    }

    return true;
  }
}
